<div class="app-table-container">
  <!-- Search Bar and Create Button -->
  @if (config.showSearch || config.showCreate) {
  <div class="app-table__toolbar">
    <div class="app-table__search-container">
      @if (config.showSearch) {
      <div class="app-table__search-input-container">
        <ng-glyph name="search" size="20" class="app-table__search-icon"></ng-glyph>
        <input
          type="text"
          class="app-table__search-input"
          [placeholder]="config.searchPlaceholder || 'Search...'"
          [value]="searchTerm"
          (input)="onSearchInput($event)" />
      </div>
      } @if (hasFilters()) {
      <div class="app-table__filters-container">
        @for (filter of filters; track filter.key) {
        <div class="app-table__filter-item">
          <select
            class="app-table__filter-select"
            [id]="'table-filter-' + filter.key"
            [value]="activeFilters[filter.key] || ''"
            (change)="onFilterInput(filter.key, $event)">
            <option value="">{{ filter.placeholder || "All" }}</option>
            @for (option of filter.options; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </div>
        }
      </div>
      }
    </div>
    <div class="app-table__actions-container">
      <!-- Column Visibility Toggle Button -->
      <button class="app-table__column-toggle-btn" (click)="toggleColumnToggle()" type="button" title="Toggle columns">
        <ng-glyph name="view_column" size="20"></ng-glyph>
      </button>

      <!-- Date Range Filter Toggle Button -->
      @if (config.showDateRangeFilter) {
      <button
        class="app-table__date-range-filter-btn"
        (click)="toggleDateRangeFilter()"
        type="button"
        [attr.title]="config.dateRangeFilterLabel || 'Filter by date range'"
        [class.app-table__date-range-filter-btn--active]="hasDateRangeFilter()">
        <ng-glyph name="calendar_today" size="20"></ng-glyph>
      </button>
      } @if (config.showCreate) {
      <button class="app-table__create-btn" (click)="onCreateClick()" type="button">
        <ng-glyph [name]="config.createButtonIcon || 'add'" size="20"></ng-glyph>
        <span>{{ config.createButtonLabel || "Create" }}</span>
      </button>
      }
    </div>
  </div>
  }

  <!-- Column Visibility Dropdown -->
  @if (showColumnToggle) {
  <div class="app-table__column-toggle-dropdown">
    <div class="app-table__column-toggle-header">
      <h4>Visible Columns</h4>
      <button class="app-table__column-toggle-close" (click)="toggleColumnToggle()">
        <ng-glyph name="close" size="16"></ng-glyph>
      </button>
    </div>
    <div class="app-table__column-toggle-list">
      @for (column of columns; track column.key) {
      <div class="app-table__column-toggle-item">
        <label class="app-table__column-toggle-label">
          <input type="checkbox" [checked]="isColumnVisible(column.key)" (change)="toggleColumnVisibility(column.key)" />
          <span class="app-table__column-toggle-text">{{ column.header }}</span>
        </label>
      </div>
      }
    </div>
  </div>
  }

  <!-- Date Range Filter Dropdown -->
  @if (showDateRangeFilter) {
  <div class="app-table__date-range-filter-dropdown">
    <div class="app-table__date-range-filter-header">
      <h4>Filter by Date Range</h4>
      <button class="app-table__date-range-filter-close" (click)="closeDateRangeFilter()">
        <ng-glyph name="close" size="16"></ng-glyph>
      </button>
    </div>
    <div class="app-table__date-range-filter-body">
      <div class="app-table__date-range-filter-group">
        <label class="app-table__date-range-filter-label" for="dateFrom">From</label>
        <div class="app-table__date-range-input-wrapper">
          <input
            id="dateFrom"
            type="datetime-local"
            class="app-table__date-range-input"
            [value]="dateRangeFilter.from?.slice(0, 16) || ''"
            (change)="onDateRangeChange($event, 'from')"
            placeholder="Select from date" />
        </div>
      </div>
      <div class="app-table__date-range-filter-group">
        <label class="app-table__date-range-filter-label" for="dateTo">To</label>
        <div class="app-table__date-range-input-wrapper">
          <input
            id="dateTo"
            type="datetime-local"
            class="app-table__date-range-input"
            [value]="dateRangeFilter.to?.slice(0, 16) || ''"
            (change)="onDateRangeChange($event, 'to')"
            placeholder="Select to date" />
        </div>
      </div>
      @if (hasDateRangeFilter()) {
      <div class="app-table__date-range-filter-actions">
        <button class="app-table__date-range-clear-btn" (click)="clearDateRangeFilter()" type="button">
          <ng-glyph name="delete" size="16"></ng-glyph>
          <span>Clear Filter</span>
        </button>
      </div>
      }
    </div>
  </div>
  }

  <!-- Table -->
  <table class="app-table">
    <thead class="app-table__header">
      <tr class="app-table__header-row" cdkDropList cdkDropListOrientation="horizontal" (cdkDropListDropped)="onColumnDrop($event)">
        @if (hasSelectableRows()) {
        <th class="app-table__header-cell app-table__header-cell--select" [style.width]="'52px'">
          <input
            type="checkbox"
            class="app-table__select-checkbox"
            [checked]="areAllVisibleRowsSelected()"
            [indeterminate]="hasSomeVisibleRowsSelected()"
            (change)="toggleSelectAllVisible($event)"
            aria-label="Select all visible rows" />
        </th>
        } @if (hasExpandableRows()) {
        <th class="app-table__header-cell app-table__header-cell--expand" [style.width]="'52px'">
          <span class="app-table__header-text">Details</span>
        </th>
        } @for (column of getVisibleColumns(); track column.key) {
        <th
          class="app-table__header-cell"
          [style.width]="column.width"
          [style.textAlign]="column.align || 'left'"
          (click)="onSort(column, $event)"
          [class.app-table__header-cell--sortable]="column.sortable"
          cdkDrag>
          <div class="app-table__header-content" [style.justifyContent]="getHeaderContentJustify(column)">
            <span class="app-table__header-text">{{ column.header }}</span>
            @if (column.sortable) {
            <div class="app-table__sort-indicators">
              <ng-glyph class="app-table__sort-icon" [name]="getSortIcon(column)" size="18"></ng-glyph>
              @if (getSortPriority(column) !== null && multiSort.length > 1) {
              <span class="app-table__sort-priority">{{ getSortPriority(column) }}</span>
              }
            </div>
            }
            <ng-glyph class="app-table__drag-handle" name="drag_indicator" size="16" cdkDragHandle></ng-glyph>
          </div>
        </th>
        } @if (config.showActions) {
        <th
          class="app-table__header-cell app-table__header-cell--actions"
          [style.width]="getActionsWidth()"
          [style.minWidth]="getActionsWidth()">
          <span class="app-table__header-text">Actions</span>
        </th>
        }
      </tr>
    </thead>
    <tbody class="app-table__body">
      @if (config.loading) {
      <tr class="app-table__loading-row">
        <td [attr.colspan]="getColumnSpan()" class="app-table__loading-cell">
          <div class="app-table__loading-content">
            <ng-glyph name="refresh" size="24" class="app-table__loading-icon" style="animation: spin 1s linear infinite"></ng-glyph>
            <span>Loading...</span>
          </div>
        </td>
      </tr>
      } @else { @if (displayedData.length === 0) {
      <tr class="app-table__empty-row">
        <td [attr.colspan]="getColumnSpan()" class="app-table__empty-cell">
          <div class="app-table__empty-content">
            <ng-glyph name="info" size="24" class="app-table__empty-icon"></ng-glyph>
            <span>{{ config.emptyMessage || "No data available" }}</span>
          </div>
        </td>
      </tr>
      } @else { @for (item of displayedData; track item; let i = $index) {
      <tr class="app-table__row">
        @if (hasSelectableRows()) {
        <td class="app-table__cell app-table__cell--select">
          <input
            type="checkbox"
            class="app-table__select-checkbox"
            [checked]="isRowSelected(item)"
            [disabled]="!isRowSelectable(item)"
            (change)="toggleRowSelection(item, $event)"
            aria-label="Select row" />
        </td>
        } @if (hasExpandableRows()) {
        <td class="app-table__cell app-table__cell--expand">
          <button
            class="app-table__expand-btn"
            type="button"
            [attr.aria-expanded]="isRowExpanded(item)"
            [attr.aria-label]="getExpandButtonLabel()"
            (click)="toggleRowExpanded(item, $event)">
            <ng-glyph [name]="isRowExpanded(item) ? 'expand_less' : 'expand_more'" size="18"></ng-glyph>
          </button>
        </td>
        } @for (column of getVisibleColumns(); track column.key) {
        <td
          class="app-table__cell"
          [style.textAlign]="column.align || 'left'"
          [class.app-table__cell--inline-edit]="isInlineEditableCell(column, item)">
          @if (isInlineEditableCell(column, item)) { @if (column.editType === "select") {
          <select
            class="app-table__inline-select"
            [value]="getDraftCellValue(item, column) ?? ''"
            (change)="setDraftCellValue(item, column, $event)">
            @for (option of getEditOptions(column, item); track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
          } @else { @if (column.editType === "number") {
          <input
            class="app-table__inline-input"
            appQtyInput
            type="number"
            [placeholder]="column.placeholder || ''"
            [attr.min]="column.min ?? null"
            [attr.max]="column.max ?? null"
            [attr.step]="column.step ?? null"
            [value]="getDraftCellValue(item, column) ?? ''"
            (input)="setDraftCellValue(item, column, $event)" />
          } @else {
          <input
            class="app-table__inline-input"
            type="text"
            [placeholder]="column.placeholder || ''"
            [value]="getDraftCellValue(item, column) ?? ''"
            (input)="setDraftCellValue(item, column, $event)" />
          } } } @else if (column.cellTemplate) {
          <span [ngClass]="getCellClass(column, item)">
            @if (getCellIcon(column, item); as iconName) {
            <ng-glyph [name]="iconName" size="14"></ng-glyph>
            } {{ column.cellTemplate(item) }}
          </span>
          } @else {
          <span [ngClass]="getCellClass(column, item)">
            @if (getCellIcon(column, item); as iconName) {
            <ng-glyph [name]="iconName" size="14"></ng-glyph>
            } {{ getNestedValue(item, column.key) }}
          </span>
          }
        </td>
        } @if (config.showActions) {
        <td class="app-table__cell app-table__cell--actions" [style.width]="getActionsWidth()" [style.minWidth]="getActionsWidth()">
          <div class="app-table__actions">
            @for (action of config.actionButtons || []; track action.label) { @if (!action.visible || action.visible(item)) {
            <button
              class="app-table__action-btn"
              [class.app-table__action-btn--disabled]="action.disabled?.(item)"
              [disabled]="action.disabled?.(item)"
              (click)="onActionClick(action, item)"
              [title]="action.label"
              type="button">
              <ng-glyph [name]="action.icon" size="18"></ng-glyph>
            </button>
            } }
          </div>
        </td>
        }
      </tr>
      @if (hasExpandableRows() && isRowExpanded(item) && config.expandable?.expandedRowTemplate) {
      <tr class="app-table__expanded-row">
        <td class="app-table__expanded-cell" [attr.colspan]="getColumnSpan()">
          <div class="app-table__expanded-content">
            <ng-container
              [ngTemplateOutlet]="config.expandable!.expandedRowTemplate"
              [ngTemplateOutletContext]="{ $implicit: item }"></ng-container>
          </div>
        </td>
      </tr>
      } } } }
    </tbody>
  </table>

  <!-- Pagination -->
  @if (!config.loading && displayedData.length > 0 && config.showPagination !== false) {
  <div class="app-table__pagination">
    <div class="app-table__pagination-info">
      Showing {{ (pagination.page - 1) * pagination.limit + 1 }} to {{ getEndRange() }} of {{ pagination.total }} entries
    </div>
    <div class="app-table__pagination-size">
      <span class="app-table__pagination-label">Rows per page</span>
      <div class="app-table__pagination-select-wrap">
        <select class="app-table__pagination-select" [value]="currentPageSize" (change)="onPageSizeChange($event)">
          @for (size of pageSizeOptions; track size) {
          <option [value]="size" [selected]="size === currentPageSize">{{ size }}</option>
          }
        </select>
        <ng-glyph class="app-table__pagination-caret" name="expand_more" size="16"></ng-glyph>
      </div>
    </div>
    <div class="app-table__pagination-controls">
      <button
        class="app-table__pagination-btn"
        [disabled]="currentPage <= 1"
        (click)="onPageChange(currentPage - 1)"
        title="Previous page">
        <ng-glyph name="chevron_left" size="18"></ng-glyph>
      </button>

      @for (page of getPaginationRange(); track page) {
      <button
        class="app-table__pagination-btn"
        [class.app-table__pagination-btn--active]="page === currentPage"
        (click)="onPageChange(page)"
        [disabled]="page === currentPage">
        {{ page }}
      </button>
      }

      <button
        class="app-table__pagination-btn"
        [disabled]="currentPage >= pagination.pages"
        (click)="onPageChange(currentPage + 1)"
        title="Next page">
        <ng-glyph name="chevron_right" size="18"></ng-glyph>
      </button>
    </div>
  </div>
  }
</div>
